import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Edit3, CheckCircle, Clock, AlertTriangle, Share2, Scale } from 'lucide-react';

interface ReportHeaderProps {
  report: {
    id: string;
    client_name: string;
    claimant_name: string;
    business_address: string;
    status: string;
  };
  onShareClick: () => void;
}

export const ReportHeader: React.FC<ReportHeaderProps> = ({ report, onShareClick }) => {
  return (
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-xl font-bold text-gray-900">{report.client_name}</h1>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
            report.status === 'completed' ? 'bg-green-100 text-green-700' :
            report.status === 'causation_failed' ? 'bg-amber-100 text-amber-700' :
            report.status === 'processing' ? 'bg-blue-100 text-blue-700' :
            report.status === 'failed' ? 'bg-red-100 text-red-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {report.status === 'completed' && <CheckCircle className="w-3 h-3" />}
            {report.status === 'processing' && <Clock className="w-3 h-3" />}
            {report.status === 'causation_failed' && <AlertTriangle className="w-3 h-3" />}
            {report.status === 'causation_failed' ? 'Needs Attention' : report.status}
          </span>
        </div>
        <p className="text-sm text-gray-500">{report.claimant_name} • {report.business_address}</p>
      </div>
      
      <div className="flex items-center gap-2">
        {/* Adjuster Analysis Button - Show for completed reports */}
        {report.status === 'completed' && (
          <Link 
            to={`/adjuster/${report.id}`}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition shadow-lg shadow-purple-600/25"
            data-testid="adjuster-analysis-btn"
          >
            <Scale className="w-4 h-4" />
            Adjuster Analysis
          </Link>
        )}
        {(report.status === 'draft' || report.status === 'causation_failed' || report.status === 'processing' || report.status === 'failed') && (
          <Link 
            to={`/wizard-v2?resume=${report.id}`}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition shadow-lg shadow-blue-600/25"
          >
            <Edit3 className="w-4 h-4" />
            {report.status === 'draft' ? 'Resume' : 
             report.status === 'causation_failed' ? 'Edit & Fix' :
             report.status === 'processing' ? 'View Progress' :
             'Retry'}
          </Link>
        )}
        <button
          onClick={onShareClick}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 rounded-lg text-sm font-medium transition"
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>
      </div>
    </div>
  );
};

export default ReportHeader;
